import { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";

import { PreMintGround, preMintGroundFromEvidence, preMintSpec } from "../../services/challenge.service";
import usePanelVerdict from "../../hooks/usePanelVerdict";
import usePreMintVerdictSignatures from "../../hooks/usePreMintVerdictSignatures";
import PanelSignatureCollector from "./PanelSignatureCollector";
import PreMintVerdictSignatureTally from "./PreMintVerdictSignatureTally";
import { PopupGhostButton } from "../PopupButtons";
import PopupDialog from "../PopupDialog";
import { CalloutText, InfoCallout, SectionLabel, SegmentedChoice } from "../Settlement/settlementStyles";
import { STEP_SWAP_DISTANCE, STEP_SWAP_MS, swapInSx } from "../../styles/motion";

// Pre-mint determination (V10.1) — EcoFuturesGovernance.judgePreMint(requestId, ground, upheld,
// signatures), replacing EcoFuturesCore.judgeChallenge(requestId).
//
// judgeChallenge had no verdict: its only effect was to CLEAR the challenge, and "agreeing with
// the challenger" meant leaving it standing and reassigning by hand. judgePreMint records a real
// verdict, on a stated GROUND, and the contract acts on it:
//
//   ground SCORE      the verification is not supported by its evidence. A three-seat panel
//                     deciding by two (Governance.preMintPanel). Upheld → the verifier is put AT
//                     the dismissal bar (Admin.recordAdjudication(verifier, true) sets the counter
//                     to the threshold, EcoFuturesAdmin.sol:548-549 — not one flag of three)
//                     and the request is reassigned to the challenger, who re-verifies; the score
//                     comes from that re-verification, not from the panel.
//   ground DOCUMENTS  a folio does not check out, the guardian is not the registered owner, or
//                     the agreement was not validly signed. ONE checker (Governance.preMintChecker),
//                     one signature. Upheld → the request FAILS and the guardian is refunded.
//   not upheld        on either ground: the verification stands, the mint is unblocked, and the
//                     CHALLENGER is flagged — a failed pre-mint challenge is no longer free.
//
// The enum is { NONE, SCORE, DOCUMENTS } = 0, 1, 2 in the deployed source; the README's
// "0 = SCORE, 1 = DOCUMENTS" is wrong and NONE reverts PreMintScore.
//
// Collecting the signatures: each judge signs here and the signature goes to the backend
// (usePreMintVerdictSignatures / PreMintVerdictSignatureTally), which submits judgePreMint from
// its own wallet once one verdict has enough — no gas for the judge. Only when that endpoint
// cannot be reached does the dialog fall back to the old lane — signatures pasted between judges
// and judgePreMint sent from this wallet (usePanelVerdict / PanelSignatureCollector), a direct
// wallet transaction since Governance reads raw msg.sender, not the relay.

type GroundChoice = "score" | "documents";
type UpheldChoice = "dismiss" | "uphold";

const GROUND_OPTIONS: { value: GroundChoice; label: string }[] = [
  { value: "score", label: "Score" },
  { value: "documents", label: "Documents" },
];
const UPHELD_OPTIONS: { value: UpheldChoice; label: string }[] = [
  { value: "dismiss", label: "Dismiss" },
  { value: "uphold", label: "Uphold" },
];

export interface JudgeVerificationChallengeDialogProps {
  open: boolean;
  onClose: () => void;
  // On-chain verification request id — EcoToken.requestId, NOT a token/NFT id.
  requestId: number | string;
  // The challenge under review, read from the eco-token row the queue already loaded.
  evidence?: string | null;
  challengedBy?: string | null;
  challengedAt?: Date | string | null;
  landName?: string | null;
  // Connected wallet — the collector says whether it holds a seat.
  viewerAddress?: string | null;
  // Called once PreMintDetermined is mined, so the caller can drop the row from its queue.
  onJudged?: () => void;
}

const shortAddress = (addr: string) => `${addr.slice(0, 6)}…${addr.slice(-4)}`;

const JudgeVerificationChallengeDialog = ({
  open,
  onClose,
  requestId,
  evidence,
  challengedBy,
  challengedAt,
  landName,
  viewerAddress,
  onJudged,
}: JudgeVerificationChallengeDialogProps) => {
  // The ground the challenger stated, from the tag the verification page writes at the head of the
  // evidence. It preselects the choice below and stays changeable: the panel decides the ground.
  const statedGround = preMintGroundFromEvidence(evidence);
  const statedGroundChoice: GroundChoice | null =
    statedGround === PreMintGround.SCORE ? "score" : statedGround === PreMintGround.DOCUMENTS ? "documents" : null;
  const [groundChoice, setGroundChoice] = useState<GroundChoice | null>(statedGroundChoice);
  const [upheldChoice, setUpheldChoice] = useState<UpheldChoice | null>(null);
  // The request and wallet the verdict (and its signatures) belong to. Held here rather than read
  // off requestId because the caller clears its row on close, which would drop the builder — and
  // with it every signature — the moment the dialog shut.
  const [subject, setSubject] = useState<{ requestId: string; viewer: string | null } | null>(null);

  // Reopening the same challenge from the same wallet keeps the verdict and its signatures,
  // re-checked against the chain's current nonce and seats. Anything else starts clean.
  useEffect(() => {
    if (!open || requestId === "") {
      return;
    }
    const viewer = viewerAddress?.toLowerCase() ?? null;
    if (subject && subject.requestId === String(requestId) && subject.viewer === viewer) {
      verdict.revalidate();
      return;
    }
    setSubject({ requestId: String(requestId), viewer });
    setGroundChoice(statedGroundChoice);
    setUpheldChoice(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, requestId, viewerAddress]);

  const ground = groundChoice === "documents" ? PreMintGround.DOCUMENTS : PreMintGround.SCORE;
  const upheld = upheldChoice === "uphold";
  const verdictChosen = groundChoice !== null && upheldChoice !== null && !!subject;

  const requestIdBig = subject ? BigInt(subject.requestId) : null;

  // The server-collected lane, polled while the dialog is open on a request. Independent of the
  // ground/verdict picked below — it reads panel/threshold/tally for both grounds at once.
  const serverLane = usePreMintVerdictSignatures(requestIdBig, open && !!subject);
  const fallback = serverLane.unavailable;

  // New builder identity per (request, ground, upheld) — the hook clears collected signatures on
  // it. Only built on the fallback lane: while the server collects, nothing is gathered here.
  const build = useMemo(() => {
    if (!subject || !verdictChosen || !fallback) {
      return null;
    }
    const id = BigInt(subject.requestId);
    return () => preMintSpec(id, ground, upheld);
  }, [subject, verdictChosen, ground, upheld, fallback]);

  const verdict = usePanelVerdict(build, viewerAddress);

  useEffect(() => {
    if (verdict.status === "success") {
      // Determined — nothing left to carry into a reopen.
      setSubject(null);
      onJudged?.();
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verdict.status]);

  // The server's judgePreMint landed: isChallenged flips false on chain, however the threshold
  // was reached. There is no SEATED/DETERMINED trail to watch on the pre-mint side the way
  // RecordOutcomeDialog watches the post-mint one, so this is the lane's own chain read.
  const serverChallenged = serverLane.challenged;
  useEffect(() => {
    if (!open || fallback || !subject || serverChallenged !== false) {
      return;
    }
    setSubject(null);
    onJudged?.();
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fallback, subject, serverChallenged]);

  const handleClose = () => {
    if (verdict.isPending || serverLane.signing) {
      return;
    }
    onClose();
  };

  const raisedAt = challengedAt ? new Date(challengedAt) : null;

  const consequence = !verdictChosen
    ? null
    : !upheld
    ? "Dismissed: the verification stands, the mint is unblocked and the challenger is flagged."
    : ground === PreMintGround.SCORE
    ? "Upheld on score: the verifier's flag count is set to the dismissal bar — a Trust Admin who did not admit them " +
      "may dismiss them at once — and the request is reassigned to the challenger, who re-verifies the land. The " +
      "score comes from that re-verification."
    : "Upheld on documents: the request fails, the guardian's verification fee is refunded, and the verifier's flag " +
      "count is set to the dismissal bar.";

  return (
    <PopupDialog open={open} onClose={handleClose}>
      <p style={{ fontWeight: 600, fontSize: "20px", margin: "0 0 8px", fontFamily: "'Inter', sans-serif" }}>
        Judge this verification challenge
      </p>
      <p style={{ fontSize: "14px", lineHeight: 1.5, color: "#555", margin: "0 0 20px" }}>
        The mint of {landName ? <strong>{landName}</strong> : "this land"} is blocked while the challenge stands. A
        drawn panel decides a challenge to the score; a single drawn checker decides one to the documents. Each judge
        signs the verdict from its own wallet.
      </p>

      {/* The challenge under review, straight off the eco-token row. */}
      <Box sx={{ backgroundColor: "#FFF4E5", borderRadius: "12px", padding: "16px", mb: "20px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: "10px" }}>
          <GavelOutlinedIcon sx={{ color: "#B76E00", flexShrink: 0 }} />
          <p style={{ fontSize: "14px", fontWeight: 600, color: "#7A4100", margin: 0 }}>Challenge under review</p>
        </Box>
        <p style={{ fontSize: "14px", lineHeight: 1.6, color: "#7A4100", margin: 0, whiteSpace: "pre-wrap" }}>
          {evidence || "No evidence was recorded with this challenge."}
        </p>
        <p style={{ fontSize: "13px", color: "#A9713A", margin: "10px 0 0" }}>
          {challengedBy ? `Raised by ${shortAddress(challengedBy)}` : "Challenger unknown"}
          {raisedAt ? ` · ${raisedAt.toLocaleString()}` : ""}
        </p>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <SectionLabel>What is the challenge about?</SectionLabel>
        <SegmentedChoice
          options={GROUND_OPTIONS}
          value={groundChoice}
          onChange={setGroundChoice}
          disabled={verdict.isPending}
        />
        {statedGroundChoice && (
          <p style={{ fontSize: "13px", color: "#888", margin: 0 }}>
            Preselected from the ground the challenger stated. Change it if the evidence is about the other.
          </p>
        )}
        <SectionLabel>Verdict</SectionLabel>
        <SegmentedChoice
          options={UPHELD_OPTIONS}
          value={upheldChoice}
          onChange={setUpheldChoice}
          disabled={verdict.isPending}
        />

        {/* Keyed on the copy, so each change of ground or verdict replays a text swap instead of
            the note's words (and colour) changing under the reader in one frame. */}
        {consequence && (
          <Box
            key={consequence}
            sx={{
              ...swapInSx(),
              backgroundColor: upheld ? "#FFF4E5" : "#EAF3FF",
              border: `1px solid ${upheld ? "#F2C48D" : "#C5DBF2"}`,
              borderRadius: "12px",
              padding: "12px 16px",
            }}
          >
            <p style={{ fontSize: "13px", color: upheld ? "#7A4100" : "#1B5E9E", margin: 0, lineHeight: 1.5 }}>
              {consequence}
            </p>
          </Box>
        )}

        {verdictChosen ? (
          // The signing block takes over from "Leave it standing" as a step, not a cut. The
          // collector renders a fragment laid out by this column's gap, so the wrapper repeats it.
          <Box
            sx={{
              ...swapInSx(STEP_SWAP_MS, STEP_SWAP_DISTANCE),
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            {fallback && (
              <InfoCallout surface="white" tone="warning">
                <CalloutText>
                  The signature service is unreachable, so signatures are collected here instead: each judge signs
                  and sends theirs to whoever submits, who pays the gas.
                </CalloutText>
              </InfoCallout>
            )}
            {fallback ? (
              <PanelSignatureCollector verdict={verdict} submitLabel="Record determination" onCancel={handleClose} />
            ) : (
              <PreMintVerdictSignatureTally
                lane={serverLane}
                ground={ground}
                upheld={upheld}
                viewerAddress={viewerAddress}
                onCancel={handleClose}
              />
            )}
          </Box>
        ) : (
          <PopupGhostButton onClick={handleClose} fullWidth>
            Leave it standing
          </PopupGhostButton>
        )}
      </Box>
      <p style={{ fontSize: "13px", color: "#888", textAlign: "center", margin: "12px 0 0" }}>
        Seats are drawn from the country&apos;s Trust Admins and the GTA registry. Until the registry holds three
        members, a score challenge cannot be decided.
        {fallback
          ? " Whoever submits the determination pays the gas."
          : " Signing is free: the server records the determination on chain once enough judges agree."}
      </p>
    </PopupDialog>
  );
};

export default JudgeVerificationChallengeDialog;
